// Display names for user ids (me, the partner) and the review session; who is the admin.
// Every name comes from /api/me (store.me, store.partner): the person signed in on this device and
// the other one. Nothing here knows who "Axel" or "Roosa" is.
import type { Messages } from '../i18n';
import { useApp, type AppState } from '../state/store';

/** "Sinä" for me, the partner's name, "Tarkistussessio" for a review session, else the raw id. */
export function nameOf(id: string | undefined | null, s: Pick<AppState, 'me' | 'partner'>, t: Messages): string {
  if (!id) return '';
  if (id === s.me.id) return t.common.you;
  if (s.partner && id === s.partner.id) return s.partner.name || t.common.partner;
  if (/^(claude|review|session)/i.test(id)) return t.common.session;
  return id;
}

/** True when `id` is the other user (approvals, notes and corrections are the partner's job). */
export function isPartner(id: string | undefined | null, s: Pick<AppState, 'partner'>): boolean {
  return !!id && !!s.partner && id === s.partner.id;
}

/**
 * The admin's name when it is not me: the partner's, when the partner is the admin (or when an
 * older cached /api/me does not carry the role: there are two people, and I am not the admin).
 */
export function adminName(partner: Pick<AppState, 'partner'>['partner']): string {
  if (!partner) return '';
  return partner.role === undefined || partner.role === 'admin' ? partner.name : '';
}

/**
 * The admin (user.role in D1; Axel in the seed) also sees the review queue and Asetukset → Ylläpito. The dev
 * demo counts as admin, so local development shows everything. A device that has not heard from
 * the server yet (identity 'local', or a cached /me from before roles) is not the admin until it has.
 */
export function isAdmin(s: Pick<AppState, 'me' | 'identity'>): boolean {
  return s.identity === 'demo' || s.me.role === 'admin';
}

export function useIsAdmin(): boolean {
  return useApp(isAdmin);
}
